/**
 * Custom-reports API client (migration 146). Custom reports are instances of
 * pre-approved report KINDS (no stored SQL); running one dispatches to a scoped,
 * read-only server-side query. Everyone can list/run reports visible to their
 * company; platform admins build + toggle them.
 */

import { apiFetch } from "./apiClient";

export interface CustomReportSummary {
  report_id: number;
  name: string;
  description: string | null;
  report_kind: string;
}

export interface CustomReportColumn {
  key: string;
  label: string;
}

export interface CustomReportResult {
  report_id: number;
  name: string;
  columns: CustomReportColumn[];
  rows: Record<string, unknown>[];
  total: number;
}

export interface CustomReportParamDef {
  key: string;
  label: string;
  type: string;
  required: boolean;
}

export interface CustomReportKind {
  kind: string;
  label: string;
  description: string;
  params: CustomReportParamDef[];
  columns: CustomReportColumn[];
}

export interface CustomReportAdmin {
  report_id: number;
  name: string;
  description: string | null;
  report_kind: string;
  params: Record<string, unknown>;
  company_id: number | null;
  is_visible: boolean;
}

// --- any authenticated user ---
export function fetchVisibleCustomReports(
  token: string,
): Promise<{ reports: CustomReportSummary[] }> {
  return apiFetch("/api/reports/custom", token);
}

export function runCustomReport(token: string, id: number): Promise<CustomReportResult> {
  return apiFetch(`/api/reports/custom/${id}/run`, token, { method: "POST" });
}

// --- platform admin ---
export function fetchCustomReportKinds(token: string): Promise<{ kinds: CustomReportKind[] }> {
  return apiFetch("/api/admin/custom-reports/kinds", token);
}

export function fetchCustomReportsAdmin(
  token: string,
): Promise<{ reports: CustomReportAdmin[] }> {
  return apiFetch("/api/admin/custom-reports", token);
}

export function createCustomReport(
  token: string,
  body: {
    name: string; description?: string | null; report_kind: string;
    params: Record<string, unknown>; company_id?: number | null; is_visible?: boolean;
  },
): Promise<{ report_id: number }> {
  return apiFetch("/api/admin/custom-reports", token, {
    method: "POST", body: JSON.stringify(body),
  });
}

export function updateCustomReport(
  token: string,
  id: number,
  body: Record<string, unknown>,
): Promise<{ changed: boolean }> {
  return apiFetch(`/api/admin/custom-reports/${id}`, token, {
    method: "POST", body: JSON.stringify(body),
  });
}

export function deleteCustomReport(token: string, id: number): Promise<{ deleted: boolean }> {
  return apiFetch(`/api/admin/custom-reports/${id}`, token, { method: "DELETE" });
}
